<script setup>
import { ref, computed, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import { useAuthUserStore } from '@stores/auth';
import { useApplicationStore } from '@stores/application';
/***/
import { useForm } from '@compositions/Form';
import { useInitAuthPage } from '@compositions/Auth';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const FormGroup = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Form/FormGroup'));
const FormGroupCheckbox = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Form/FormGroupCheckbox'));
const InputPassword = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Form/InputPassword'));
const LegalFiles = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/LegalFiles'));
const RightImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/RightImage'));
const AuthContentImage = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Auth/ContentImage'));
const Icon = defineAsyncComponent(() => import(/* webpackChunkName: "components" */'@components/UI/Icon'));

// Store
const authUserStore = useAuthUserStore();
const applicationStore = useApplicationStore();

// Route
const route = useRoute();

// I18n
const { t, locale } = useI18n();

const currentApplication = computed(() => applicationStore.getCurrent);

// Langue en query
locale.value = (route.query['lang'] ?? locale.value);

// Vrai si l'utilisateur vient de la page d'activation de compte
const accountValidationToken = (route.query['account-validation-token'] ?? null);
// Vrai si l'utilisateur vient de la page de réinitialisation de mot de passe
const fromResetPassword = (route.query['from-reset-password'] === '1');
// Vrai si l'utilisateur vient de la page d'initialisation de mot de passe
const fromInitPassword = (route.query['from-init-password'] === '1');
// Vrai si l'utilisateur vient de faire une demande d'envoi de validation de l'inscription
const fromRegistrationValidationRequest = (route.query['from-register-notification'] === '1');

//
const onSuccess = ((response) => {
    if(response.user) {
        authUserStore.setCurrentUser(response.user);

        // Rediriger vers la page précédente
        redirectIfLogged();
    }
});

// Composition pour le formulaire
const {
    requesting,
    withGenericError,
    errors,
    submit
} = useForm({
    requestPath: '/auth/login',
    onSuccess
});

// Redirige l'utilisateur s'il est déjà connecté
const { authPreparing, redirectIfLogged } = useInitAuthPage({});

const formData = ref({
    email: route.query.email || null
});

onMounted(() => {
    document.title = t('page.auth.login.title') + ' - ' + currentApplication.value.name;
});

const defaultGenericMessage = t('auth.login.form.genericError');
const genericErrorMessage = ref(defaultGenericMessage);
const registrationValidationError = ref(false);

const submitRequest = ((event) => {

    registrationValidationError.value = false;

    submit(event).catch((response) => {
        const errorResponse = (response && response.response && response.response.data && response.response.data.exceptionName) ? response.response.data : null;

        // Gestion de l'erreur d'inscription non confirmée
        if(errorResponse && errorResponse.exceptionName === 'RegistrationNotConfirmedException') {
            genericErrorMessage.value = errorResponse.message || defaultGenericMessage;
            if(errorResponse.exceptionName === 'RegistrationNotConfirmedException') {
                registrationValidationError.value = true;
            }
        } else {
            genericErrorMessage.value = defaultGenericMessage;
        }
    });
});

// Retourne l'URL pour renvoyer le mail de validation de l'inscription
const sendValidationRegistrationNotificationUrl = computed(() => {

    const baseUrl = document.location.protocol + '//' + document.location.hostname;
    const url = new URL('/auth/register/notification', baseUrl);

    url.searchParams.set('email', formData.value.email);

    return url.toString();

});

// Retourne l'erreur à afficher dans le template
const formError = computed(() => {
    let error = genericErrorMessage.value;

    // Gestion de l'erreur de validation de l'inscription
    if(registrationValidationError.value) {
        error += ' ' + t('auth.login.form.registrationValidationError', {
            url: sendValidationRegistrationNotificationUrl.value
        });
    }

    return error;
});
</script>

<template>
    <loader v-if="authPreparing"></loader>
    <div class="auth-container" v-else>
        <auth-content-image></auth-content-image>
        <div
            class="auth_content"
        >
            <div class="auth_title">
                <h1 class="first-title">{{ $t('auth.login.form.title') }}</h1>
                <p>
                    {{ $t('auth.login.form.register.call') }}
                    <router-link
                        :to="{ name: 'auth.register' }"
                        title=""
                    >
                        {{ $t('auth.login.form.register.button.label') }}
                    </router-link>
                </p>
            </div>
            <form method="post" @submit.prevent="submitRequest($event)" class="form-auth form-auth-login login_form">
                <p v-if="accountValidationToken !== null" class="generic-message generic-message-warning">
                    {{ $t('auth.login.form.accountValidationRequired') }}
                </p>
                <p v-else-if="fromResetPassword" class="generic-message generic-message-success">
                    {{ $t('auth.login.form.fromResetPassword') }}
                </p>
                <p v-else-if="fromInitPassword" class="generic-message generic-message-success">
                    {{ $t('auth.login.form.fromInitPassword') }}
                </p>
                <p v-else-if="fromRegistrationValidationRequest" class="generic-message generic-message-success">
                    {{ $t('auth.login.form.fromRegistrationValidationRequest', {
                        email: formData.email
                    }) }}
                </p>
                <p
                    v-if="withGenericError"
                    class="generic-message generic-message-error"
                    v-html="formError"
                ></p>

                <input
                    v-if="accountValidationToken !== null"
                    type="hidden"
                    name="account_validation_token"
                    :value="accountValidationToken"
                />

                <form-group
                    :label="$t('auth.login.form.fields.email.label')"
                    input-id="login-email"
                    :error="errors.email"
                    :with-icons="true"
                >
                    <icon name="envelope"></icon>
                    <input type="email" name="email" id="login-email" v-model="formData.email" autocomplete="off" required="required" />
                </form-group>

                <form-group
                    :label="$t('auth.login.form.fields.password.label')"
                    input-id="login-password"
                    :error="errors.password"
                    :with-icons="true"
                    :classes="[ 'form-group-with-label-and-link' ]"
                >
                    <template v-slot:before-input>
                        <router-link
                            :to="{ name: 'auth.forgot-password' }"
                        >
                            {{ $t('auth.login.anchors.forgotPassword.label') }}
                        </router-link>
                    </template>

                    <input-password name="password" id="login-password" :required="true" :autocomplete="true"></input-password>
                </form-group>

                <form-group-checkbox
                    :label="$t('auth.login.form.fields.rememberMe.label')"
                    input-id="login-remember"
                    :error="errors.remember"
                >
                    <input type="checkbox" name="remember" id="login-remember" />
                </form-group-checkbox>

                <div class="form-group">
                    <button type="submit" class="button button-primary-outline button-loader" :class="{ loading: requesting }">
                        <span>{{ $t('auth.login.form.fields.submit.label') }}</span>
                    </button>
                </div>
            </form>
        </div>
        <right-image></right-image>

        <div class="auth_footer">
            <legal-files></legal-files>
        </div>
    </div>

</template>
